import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Edit,
  Save,
  X,
  Lock,
  Eye,
  EyeOff,
  Shield,
  CheckCircle,
  Building,
  Globe,
  Home,
  Bookmark
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import TACSettings from '../components/profile/TACSettings';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Profile data state
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    address2: user?.address2 || '',
    zipCode: user?.zipCode || '',
    unitNumber: user?.unitNumber || '',
    buildingName: user?.buildingName || '',
    saveAsDefault: user?.saveAsDefault || false,
    city: 'Singapore',
    country: 'Singapore'
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        address2: user.address2 || '',
        zipCode: user.zipCode || '',
        unitNumber: user.unitNumber || '',
        buildingName: user.buildingName || '',
        saveAsDefault: user.saveAsDefault || false,
        city: 'Singapore',
        country: 'Singapore'
      });
    }
  }, [user]);

  // Handle postal code change for Singapore
  const handlePostalCodeChange = (zipCode) => {
    // Only allow numbers
    const numericZipCode = zipCode.replace(/\D/g, '');

    setProfileData(prev => ({
      ...prev,
      zipCode: numericZipCode,
      city: 'Singapore',
      country: 'Singapore'
    }));

    // Show success message when 6 digits entered
    if (numericZipCode.length === 6) {
      toast.success('Singapore postal code accepted!');
    }
  };

  const handleProfileSave = async () => {
    try {
      setLoading(true);
      console.log('Saving profile data:', profileData);
      const response = await api.users.updateProfile(profileData);
      console.log('Profile save response:', response);

      setIsEditing(false);
      toast.success('Profile updated successfully!');

      if (response.user) {
        const updatedUserData = {
          ...user,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          phone: response.user.phone,
          address: response.user.address,
          address2: response.user.address2,
          zipCode: response.user.zipCode,
          unitNumber: response.user.unitNumber,
          buildingName: response.user.buildingName,
          saveAsDefault: response.user.saveAsDefault,
          city: response.user.city || 'Singapore',
          country: response.user.country || 'Singapore'
        };
        console.log('Updating user context with:', updatedUserData);
        updateUser(updatedUserData);
      }
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Profile update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await api.users.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      toast.success('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
      console.error('Password change error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 ">
      {/* Header */}
      <div className="bg-orange-600 text-white py-16 mb-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h1 className="text-6xl font-bold mb-6">Profile Settings</h1>
            <p className="text-2xl text-orange-100 mb-8 max-w-4xl mx-auto">
              Manage your account information, security settings, and personal details
            </p>
            
            {/* Tab Navigation */}
            <div className="flex justify-center space-x-8 mt-8">
              {['profile', 'security'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-bold text-lg transition-all ${
                    activeTab === tab
                      ? 'bg-white text-orange-600'
                      : 'bg-orange-700 text-orange-100 hover:bg-orange-600'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-xl p-10 mb-12"
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Profile Information</h2>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex items-center px-6 py-3 rounded-lg font-bold transition-colors ${
                  isEditing 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {isEditing ? <X className="w-5 h-5 mr-2" /> : <Edit className="w-5 h-5 mr-2" />}
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>

            {/* User Info Preview */}
            <div className="flex items-center space-x-6 mb-8 p-6 bg-orange-50 rounded-lg">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                {user?.firstName?.[0] || 'U'}
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{user?.firstName} {user?.lastName}</div>
                <div className="text-lg text-gray-600">{user?.email}</div>
                <div className="text-sm text-orange-600 font-medium">{user?.role?.charAt(0).toUpperCase()}{user?.role?.slice(1)} Account</div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <User className="w-6 h-6 mr-2 text-orange-600" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                    disabled={!isEditing}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter first name"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                    disabled={!isEditing}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Enter last name"
                  />
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      disabled={!isEditing}
                      required
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      disabled={!isEditing}
                      required
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="+65 1234 5678"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Singapore Address Information Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <MapPin className="w-6 h-6 mr-2 text-orange-600" />
                  Singapore Address
                </h3>
              </div>

              {/* Info Banner */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
                <Globe className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Service available in Singapore only</p>
                  <p className="text-sm text-blue-700 mt-1">Please enter your Singapore address for service delivery</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.zipCode}
                    onChange={(e) => handlePostalCodeChange(e.target.value)}
                    disabled={!isEditing}
                    maxLength={6}
                    pattern="[0-9]{6}"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="123456"
                  />
                  {isEditing && (
                    <p className="text-sm text-gray-500 mt-1">
                      Enter 6-digit Singapore postal code
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Country
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value="Singapore"
                      disabled
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Building Name
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={profileData.buildingName}
                      onChange={(e) => setProfileData({...profileData, buildingName: e.target.value})}
                      disabled={!isEditing}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="e.g., Marina Bay Residences"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Home className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      disabled={!isEditing}
                      required
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Block and street name"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Unit Number
                  </label>
                  <input
                    type="text"
                    value={profileData.unitNumber}
                    onChange={(e) => setProfileData({...profileData, unitNumber: e.target.value})}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="#12-345"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-base font-semibold text-gray-700 mb-2">
                    Additional Address Info (Optional)
                  </label>
                  <input
                    type="text"
                    value={profileData.address2}
                    onChange={(e) => setProfileData({...profileData, address2: e.target.value})}
                    disabled={!isEditing}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg disabled:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Apartment, suite, floor, etc."
                  />
                </div>

              </div>

              {isEditing && (
                <div className="mt-6 flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="saveAsDefault"
                    checked={profileData.saveAsDefault}
                    onChange={(e) => setProfileData({...profileData, saveAsDefault: e.target.checked})}
                    className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="saveAsDefault" className="ml-3 flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                    <Bookmark className="w-4 h-4 mr-2 text-blue-600" />
                    Save as default address for orders
                  </label>
                </div>
              )}
            </div>

            {isEditing && (
              <div className="mt-10 flex justify-end space-x-6">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-8 py-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-bold text-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProfileSave}
                  disabled={loading}
                  className="flex items-center px-8 py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-bold text-lg disabled:opacity-50"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Password Change Section */}
            <div className="bg-white rounded-xl shadow-xl p-10">
              <div className="flex items-center mb-8">
                <Shield className="w-10 h-10 text-orange-600 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Change Password</h2>
                  <p className="text-lg text-gray-600">Update your password to keep your account secure</p>
                </div>
              </div>

              <div className="space-y-6 max-w-md">
                <div>
                  <label className="block text-lg font-bold text-gray-700 mb-4">Current Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-5 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full pl-12 pr-12 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                      className="absolute right-4 top-5 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-700 mb-4">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-5 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="w-full pl-12 pr-12 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                      className="absolute right-4 top-5 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-700 mb-4">Confirm New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-5 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="w-full pl-12 pr-12 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                      className="absolute right-4 top-5 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  onClick={handlePasswordChange}
                  disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  className="w-full flex items-center justify-center px-8 py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Shield className="w-5 h-5 mr-2" />
                  {loading ? 'Changing Password...' : 'Change Password'}
                </button>
              </div>
            </div>

            {/* Two-Factor Authentication Settings */}
            <TACSettings />

            {/* Security Information */}
            <div className="bg-white rounded-xl shadow-xl p-10">
              <div className="flex items-center mb-8">
                <CheckCircle className="w-10 h-10 text-green-600 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Account Security</h2>
                  <p className="text-lg text-gray-600">Your account security status and recommendations</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between p-6 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Email Verified</h3>
                    <p className="text-gray-600">Your email address has been verified</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>

                <div className="flex items-center justify-between p-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Account Active</h3>
                    <p className="text-gray-600">Your account is active and in good standing</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                </div>

                <div className="p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Security Recommendations</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      Use a strong password with at least 8 characters
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      Change your password regularly
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      Don't share your login credentials with anyone
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;